import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api';
import { TEMPLATE_MD } from '../lib/contractTemplate';

// Hook: track whether the viewport is below a breakpoint (default 768px
// — typical tablet/mobile boundary). Drives the responsive 2-column → 1
// -column flip + sidebar stickiness on the signing page.
function useIsMobile(breakpointPx = 768) {
  const get = () => typeof window !== 'undefined' && window.innerWidth < breakpointPx;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    const onResize = () => setIsMobile(get());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpointPx]);
  return isMobile;
}

// Substitute {{placeholders}} into the contract template, mirroring the
// defaults used by bandn-backend's contractPdf.js so the signing page
// and the generated PDF show identical content. Placement fields that
// haven't been wired through AUHCA yet fall back to the same defaults
// the PDF uses.
function substituteTemplate({ candidate, employer, signedName, address, idNumber }) {
  const placement = (employer && employer.placement) || {};
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const salaryAnnual = placement.salaryAnnual || '78,400';
  const ordHours = Number(placement.ordinaryHoursPerWeek) || 38;
  const salaryHourly = placement.salaryHourly ||
    ((Number(String(salaryAnnual).replace(/[,\s]/g, '')) / ordHours / 52) || 0).toFixed(2);

  // Whole ", identification number X" run is conditional — drops out
  // cleanly (no orphan comma) when the candidate leaves the optional
  // passport field blank.
  const idTrimmed = (idNumber || '').trim();
  const candidateIdClause = idTrimmed
    ? `, identification number **${idTrimmed}**`
    : '';

  const subs = {
    candidateFullName: signedName || (candidate && candidate.fullName) || '',
    candidateAddress: address || (candidate && candidate.address) || '',
    candidateIdClause,
    candidateIdNumber: idTrimmed,
    employerName: (employer && employer.name) || 'Employer',
    employerAddress: (employer && employer.address) || '[employer street address]',
    employerState: (employer && employer.state) || 'the Australian Capital Territory',
    employerContactName: (employer && employer.contactName) || '[Director name]',
    employerContactPosition: (employer && employer.contactPosition) || 'Director',
    contractDate: today,
    employerName: (employer && employer.name) || 'Employer',
    employerAddress: (employer && employer.address) || '[employer street address]',
    employerState: (employer && employer.state) || 'the Australian Capital Territory',
    employerContactName: (employer && employer.contactName) || '[Director name]',
    employerContactPosition: (employer && employer.contactPosition) || 'Director',
    contractDate: today,
    positionTitle: placement.positionTitle || 'Personal Care Assistant',
    anzscoCode: placement.anzscoCode || '423313',
    anzscoTitle: placement.anzscoTitle || 'Personal Care Assistant',
    commencementDate: placement.commencementDate || '2 November 2026',
    employmentType: placement.employmentType || 'full-time',
    primaryWorksite: placement.primaryWorksite || '23 Mahony Court, Weston ACT 2611, Australia',
    ordinaryHoursPerWeek: String(ordHours),
    salaryAnnual: String(salaryAnnual),
    salaryHourly: String(salaryHourly),
    classificationLevel: placement.classificationLevel || '5 (Specialist)',
    probationMonths: String(placement.probationMonths || 6),
    signedAt: today,
    // Replace signature anchors with a visual placeholder line — the
    // candidate signs via the modal below, not by clicking inside the
    // text. Keeping these as plain underscores matches what a paper
    // contract would look like.
    candidateSignatureBlock: '_________________________',
    employerSignatureBlock: '_________________________',
  };
  return Object.entries(subs).reduce(
    (acc, [k, v]) => acc.replaceAll(`{{${k}}}`, v),
    TEMPLATE_MD
  );
}

// Bandn candidate signing page. Public, no auth — the refId in the URL
// path is the bearer (matches the AUHCA integration contract). Reached
// via the email link AUHCA sends when admin marks contract sent.
//
// Layout follows src/designs/Employment Agreement Signing.html:
//   - left column: agreement body + pre-filled candidate details
//   - right column: "Your details" sidebar + Sign / Complete CTAs
//   - signature modal: Draw / Type / Upload tabs, all normalised to PNG
//     data URLs before submit
//
// Three end states: pre-sign (form) → signing modal → post-sign (done).

const ORANGE = '#EE6C5A';
const WARM_BG = '#FBF6F0';
const CARD_BG = '#FFFFFF';
const INK_900 = '#1F1D1B';
const INK_700 = '#3D3935';
const INK_500 = '#6E6A66';
const INK_300 = '#A9A39B';
const INK_BORDER = '#E2DCD4';
const SUCCESS = '#188756';
const SUCCESS_BG = '#E5F4EC';

// Trigger a browser download for a base64-encoded PDF. Decodes the
// base64 into a Blob and uses a transient <a> click — works without
// new tabs, popup blockers, or server round-trips.
const downloadBase64Pdf = (base64, filename) => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'signed-contract.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const SignAgreement = () => {
  const { refId } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | ready | submitting | done | error
  const [details, setDetails] = useState(null);   // { candidate, employer }
  const [error, setError] = useState('');

  // Editable form fields — pre-filled from details, but candidate can
  // correct anything before signing.
  const [signedName, setSignedName] = useState('');
  const [address, setAddress] = useState('');
  const [idNumber, setIdNumber] = useState('');

  // Responsive layout hint — MUST be called unconditionally at the top
  // of the component (React rules-of-hooks). The render functions below
  // for loading/error/done phases close over `isMobile` for their own
  // padding/sizing too.
  const isMobile = useIsMobile(768);

  // Substitute the contract template — derived from candidate +
  // employer + form values. Memoised because it scans 16KB of markdown
  // on every render otherwise.
  const fullAgreementMd = useMemo(
    () => substituteTemplate({
      candidate: details?.candidate,
      employer: details?.employer,
      signedName,
      address,
      idNumber,
    }),
    [details, signedName, address, idNumber]
  );

  // Signature state
  const [showSigModal, setShowSigModal] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [signatureMode, setSignatureMode] = useState(null);

  // Post-sign download state — populated when submit succeeds so the
  // success card can offer an instant "Download PDF copy" button.
  const [signedPdf, setSignedPdf] = useState(null);  // { base64, filename }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getSigningDetails(refId);
        if (cancelled) return;
        const data = res.data || {};
        setDetails(data);
        setSignedName(data.candidate?.fullName || '');
        setAddress(data.candidate?.address || '');
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load this signing link.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [refId]);

  const onApplySignature = (dataUrl, mode) => {
    setSignatureDataUrl(dataUrl);
    setSignatureMode(mode);
    setShowSigModal(false);
  };

  const completeDisabled = !signatureDataUrl || !signedName.trim();

  const onComplete = async () => {
    setPhase('submitting');
    setError('');
    try {
      const res = await apiService.submitSigning(refId, {
        signedName: signedName.trim(),
        address: address.trim() || undefined,
        idNumber: idNumber.trim() || undefined,
        signedAt: new Date().toISOString(),
        signatureDataUrl,
        signatureMode,
      });
      const data = res.data || {};
      setPhase('done');
      setDetails((prev) => ({ ...prev, _signedAt: data.signedAt }));
      // Keep the PDF in memory so the success card can offer an
      // instant download — no round-trip needed. Backend returns
      // pdfBase64 (~50-100KB for our 1-page contract).
      if (data.pdfBase64) {
        setSignedPdf({ base64: data.pdfBase64, filename: data.pdfFilename || 'signed-contract.pdf' });
      }
    } catch (err) {
      setError(err.message || 'Could not submit the signed contract.');
      setPhase('ready');
    }
  };

  if (phase === 'loading') {
    return <CenteredShell><p style={{ color: INK_500 }}>Loading your agreement…</p></CenteredShell>;
  }

  if (phase === 'error') {
    return (
      <CenteredShell>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>This signing link is no longer valid</h2>
        <p style={{ color: INK_500, marginBottom: 16 }}>{error}</p>
        <p style={{ fontSize: 13, color: INK_500 }}>If you believe this is an error, please contact the team that sent you the signing email.</p>
      </CenteredShell>
    );
  }

  if (phase === 'done') {
    const employerName = details?.employer?.name || 'your employer';
    const candidateEmail = details?.candidate?.email;
    return (
      <CenteredShell>
        <div style={{ background: SUCCESS_BG, border: `1px solid ${SUCCESS}33`, borderRadius: 14, padding: 28, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: SUCCESS, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 12 }}>✓</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: INK_900, marginBottom: 6 }}>Agreement signed</h2>
          <p style={{ color: INK_700, marginBottom: 18 }}>
            Thank you, {signedName.split(' ')[0]}. Your employment agreement with <strong>{employerName}</strong> has been signed and saved.
          </p>

          {signedPdf && (
            <button
              type="button"
              onClick={() => downloadBase64Pdf(signedPdf.base64, signedPdf.filename)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                border: 'none', background: ORANGE, color: '#fff', borderRadius: 10,
                padding: '11px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                marginBottom: 18,
              }}
            >
              ⬇ Download PDF copy
            </button>
          )}

          <p style={{ color: INK_500, fontSize: 13, marginBottom: 4 }}>
            We've also sent a copy to {candidateEmail || 'your inbox'}.
          </p>
          <p style={{ color: INK_500, fontSize: 13 }}>You can close this tab.</p>
        </div>
      </CenteredShell>
    );
  }

  // phase === 'ready' | 'submitting' — render the main signing layout.
  // isMobile + fullAgreementMd are declared at the top of the component
  // (React rules-of-hooks); reusing them here.
  const employer = details?.employer || {};

  return (
    <div style={{ minHeight: '100vh', background: WARM_BG, color: INK_900, fontFamily: '"Inter", system-ui, sans-serif' }}>
      <div style={{
        maxWidth: 1080,
        margin: '0 auto',
        padding: isMobile ? '24px 14px 56px' : '40px 24px 80px',
      }}>
        <header style={{ marginBottom: isMobile ? 20 : 28 }}>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', color: ORANGE, textTransform: 'uppercase', marginBottom: 6 }}>
            Employment Agreement
          </p>
          <h1 style={{
            fontSize: isMobile ? 22 : 30,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: INK_900,
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}>
            {employer.name ? `${employer.name}: Placement Contract` : 'Placement Contract'}
          </h1>
          <p style={{ color: INK_500, fontSize: isMobile ? 13 : 14, marginTop: 6 }}>
            Please review your details and the agreement, then sign below.
          </p>
        </header>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 360px',
          gap: isMobile ? 16 : 24,
          alignItems: 'start',
        }}>
          {/* ── Left column: agreement detail + form ── */}
          <main style={{
            background: CARD_BG,
            border: `1px solid ${INK_BORDER}`,
            borderRadius: 16,
            padding: isMobile ? 18 : 32,
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: ORANGE, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Your details
            </h3>
            <p style={{ color: INK_500, fontSize: 13, marginBottom: 16 }}>
              We pre-filled this from your candidate profile. Edit anything that's not right before you sign.
            </p>

            <FieldGroup>
              <Field label="Full legal name *" value={signedName} onChange={setSignedName} placeholder="e.g. Joan Adekunle" />
              <Field label="Residential address" value={address} onChange={setAddress} placeholder="Street, city, country" textarea />
              <Field
                label="Passport / National ID number (optional)"
                value={idNumber}
                onChange={setIdNumber}
                placeholder="e.g. A12345678"
              />
            </FieldGroup>

            <hr style={{ border: 0, borderTop: `1px solid ${INK_BORDER}`, margin: '28px 0' }} />

            <h3 style={{ fontSize: 14, fontWeight: 600, color: ORANGE, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              The agreement
            </h3>
            <p style={{ color: INK_700, fontSize: 13.5, lineHeight: 1.6, marginBottom: 12 }}>
              This Agreement is made between <strong>{employer.name || 'the Employer'}</strong> (the <em>"Employer"</em>) and <strong>{signedName || 'you'}</strong> (the <em>"Employee"</em>).
            </p>
            <p style={{ color: INK_700, fontSize: 13.5, lineHeight: 1.6, marginBottom: 16 }}>
              The Employer engages the Employee in a direct-care role at the Employer's facility, subject to the terms below.
            </p>

            {/* Key facts block — the things a candidate most needs to
                see before signing. Values mirror the placement defaults
                in MULTI_TENANT_CARE/backend/src/lib/contractPdf.js. */}
            <div style={{ background: WARM_BG, border: `1px solid ${INK_BORDER}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <p style={{ fontSize: 11.5, fontWeight: 600, color: INK_500, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Key terms
              </p>
              <KeyFactRow label="Position">Personal Care Assistant (ANZSCO 423313)</KeyFactRow>
              <KeyFactRow label="Employment type">Full-time, minimum 38 ordinary hours per week</KeyFactRow>
              <KeyFactRow label="Classification">Aged Care Award 2010 (MA000018), Direct Care Level 5 (Specialist)</KeyFactRow>
              <KeyFactRow label="Annual base salary">AUD 78,400 (above the Core Skills Income Threshold and the award rate)</KeyFactRow>
              <KeyFactRow label="Commencement date">2 November 2026</KeyFactRow>
              <KeyFactRow label="Probation">6 months from commencement</KeyFactRow>
              <KeyFactRow label="Primary worksite">23 Mahony Court, Weston ACT 2611, Australia</KeyFactRow>
              <KeyFactRow label="Superannuation">12% of ordinary time earnings (legislated rate from 1 July 2025)</KeyFactRow>
              <KeyFactRow label="Governing law">Australian Capital Territory and Commonwealth of Australia</KeyFactRow>
            </div>

            <details style={{ marginTop: 10 }} open>
              <summary style={{ color: ORANGE, cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                Show the full agreement
              </summary>
              <p style={{ color: INK_500, fontSize: 12, marginTop: 8, marginBottom: 12 }}>
                This is the complete agreement you're about to sign. Scroll through and review before signing below. After signing you'll get the same content as a signed PDF.
              </p>
              <div style={{
                background: '#fff',
                border: `1px solid ${INK_BORDER}`,
                borderRadius: 10,
                padding: isMobile ? 14 : 22,
                maxHeight: isMobile ? 360 : 520,
                overflowY: 'auto',
                fontSize: isMobile ? 12.5 : 13,
                lineHeight: 1.6,
                color: INK_700,
              }}>
                <ContractMarkdown md={fullAgreementMd} />
              </div>
            </details>

            <hr style={{ border: 0, borderTop: `1px solid ${INK_BORDER}`, margin: '28px 0' }} />

            <h3 style={{ fontSize: 14, fontWeight: 600, color: ORANGE, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Sign this document
            </h3>

            {!signatureDataUrl ? (
              <button
                type="button"
                onClick={() => setShowSigModal(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  border: `1px solid ${INK_BORDER}`, background: '#fff', borderRadius: 9,
                  padding: '10px 16px', fontSize: 14, fontWeight: 500, color: INK_900, cursor: 'pointer',
                }}
              >
                ✎ Add your signature
              </button>
            ) : (
              <div style={{ border: `1px solid ${INK_BORDER}`, borderRadius: 12, padding: 14, background: '#fff', maxWidth: 360 }}>
                <img src={signatureDataUrl} alt="Your signature" style={{ display: 'block', maxWidth: '100%', maxHeight: 80 }} />
                <p style={{ fontSize: 11, color: INK_500, marginTop: 8 }}>Signed by {signedName}</p>
                <button
                  type="button"
                  onClick={() => setShowSigModal(true)}
                  style={{ border: 'none', background: 'none', color: ORANGE, fontSize: 12.5, padding: 0, marginTop: 6, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Change signature
                </button>
              </div>
            )}
          </main>

          {/* ── Right column: sticky sidebar with the Complete CTA ── */}
          <aside style={isMobile ? {} : { position: 'sticky', top: 24 }}>
            <div style={{ background: CARD_BG, border: `1px solid ${INK_BORDER}`, borderRadius: 16, padding: 22 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: ORANGE, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                For the Employer
              </h3>
              <p style={{ color: INK_900, fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{employer.name || '—'}</p>
              {employer.state && <p style={{ color: INK_500, fontSize: 12.5 }}>{employer.state}, Australia</p>}
              {employer.contactName && <p style={{ color: INK_500, fontSize: 12.5, marginTop: 6 }}>Contact: {employer.contactName}</p>}
              {employer.contactEmail && <p style={{ color: INK_500, fontSize: 12.5 }}>{employer.contactEmail}</p>}

              {error && (
                <div style={{ marginTop: 16, padding: 10, background: '#FDECEB', border: '1px solid #F5BEB7', borderRadius: 8, fontSize: 12.5, color: '#9B2118' }}>
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={onComplete}
                disabled={completeDisabled || phase === 'submitting'}
                style={{
                  marginTop: 18, width: '100%', border: 'none', borderRadius: 10, padding: 13,
                  fontSize: 14.5, fontWeight: 600, cursor: completeDisabled ? 'not-allowed' : 'pointer',
                  background: completeDisabled ? INK_300 : ORANGE, color: '#fff', transition: 'background .15s',
                }}
              >
                {phase === 'submitting' ? 'Submitting…' : 'Complete signing'}
              </button>
              <p style={{ fontSize: 11, color: INK_500, marginTop: 8, textAlign: 'center' }}>
                Single-use link. Once you submit, the link expires.
              </p>
            </div>

            <p style={{ marginTop: 16, fontSize: 11, color: INK_500, lineHeight: 1.5, textAlign: 'center' }}>
              By signing you acknowledge you've read the eight clauses above. A signed copy will be available in your candidate portal.
            </p>
          </aside>
        </div>
      </div>

      {showSigModal && (
        <SignatureModal
          fullName={signedName}
          onClose={() => setShowSigModal(false)}
          onApply={onApplySignature}
        />
      )}
    </div>
  );
};

// ─── Layout helpers ─────────────────────────────────────────────────

const CenteredShell = ({ children }) => (
  <div style={{ minHeight: '100vh', background: WARM_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: '"Inter", system-ui, sans-serif' }}>
    <div style={{ background: CARD_BG, border: `1px solid ${INK_BORDER}`, borderRadius: 16, padding: 32, maxWidth: 460, width: '100%' }}>
      {children}
    </div>
  </div>
);

// One labelled row inside the "Key terms" block on the agreement
// preview. Two-column on wide screens, stacked on narrow.
// Minimal markdown → React renderer. Mirrors the primitives the
// PDF renderer in contractPdf.js handles (H1/H2/H3, bullets,
// paragraphs, bold + italic runs, horizontal rules). Sufficient for
// the contract template. Not a general-purpose parser.
const ContractMarkdown = ({ md }) => {
  const elements = [];
  const lines = md.split('\n');
  let inHtmlComment = false;
  let bulletGroup = null;  // accumulates consecutive <li>s into one <ul>

  const flushBullets = () => {
    if (bulletGroup && bulletGroup.length) {
      elements.push(
        <ul key={`ul-${elements.length}`} style={{ margin: '6px 0 10px 18px', padding: 0 }}>
          {bulletGroup}
        </ul>
      );
    }
    bulletGroup = null;
  };

  lines.forEach((raw, i) => {
    const line = raw.trim();

    if (line.startsWith('<!--')) { inHtmlComment = !line.includes('-->'); return; }
    if (inHtmlComment) { if (line.includes('-->')) inHtmlComment = false; return; }

    if (line === '') { flushBullets(); return; }
    if (line === '---') {
      flushBullets();
      elements.push(<hr key={i} style={{ border: 0, borderTop: `1px solid ${INK_BORDER}`, margin: '16px 0' }} />);
      return;
    }

    if (line.startsWith('# ')) {
      flushBullets();
      elements.push(
        <h2 key={i} style={{ fontSize: 17, fontWeight: 700, color: INK_900, margin: '12px 0 8px' }}>
          {parseInline(line.slice(2))}
        </h2>
      );
      return;
    }
    if (line.startsWith('## ')) {
      flushBullets();
      elements.push(
        <h3 key={i} style={{ fontSize: 14, fontWeight: 700, color: INK_900, margin: '16px 0 6px' }}>
          {parseInline(line.slice(3))}
        </h3>
      );
      return;
    }
    if (line.startsWith('### ')) {
      flushBullets();
      elements.push(
        <h4 key={i} style={{ fontSize: 13, fontWeight: 600, color: INK_700, margin: '12px 0 4px' }}>
          {parseInline(line.slice(4))}
        </h4>
      );
      return;
    }
    if (line.startsWith('- ')) {
      if (!bulletGroup) bulletGroup = [];
      bulletGroup.push(
        <li key={i} style={{ marginBottom: 4 }}>{parseInline(line.slice(2))}</li>
      );
      return;
    }
    flushBullets();
    elements.push(
      <p key={i} style={{ margin: '4px 0 8px' }}>{parseInline(line)}</p>
    );
  });
  flushBullets();
  return <>{elements}</>;
};

// Parse **bold** and *italic* spans inside a single line.
function parseInline(text) {
  const out = [];
  let i = 0;
  let buf = '';
  let bold = false;
  let italic = false;
  const flush = () => {
    if (!buf) return;
    let node = buf;
    if (bold && italic) node = <strong key={out.length}><em>{node}</em></strong>;
    else if (bold) node = <strong key={out.length}>{node}</strong>;
    else if (italic) node = <em key={out.length}>{node}</em>;
    out.push(node);
    buf = '';
  };
  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') { flush(); bold = !bold; i += 2; continue; }
    if (text[i] === '*') { flush(); italic = !italic; i += 1; continue; }
    buf += text[i++];
  }
  flush();
  return out.length ? out : '';
}

const KeyFactRow = ({ label, children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, padding: '5px 0', borderBottom: `1px dashed ${INK_BORDER}` }}>
    <div style={{ flex: '0 0 170px', fontSize: 12, color: INK_500, fontWeight: 500 }}>{label}</div>
    <div style={{ flex: '1 1 240px', fontSize: 13, color: INK_900 }}>{children}</div>
  </div>
);

const FieldGroup = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>
);

const Field = ({ label, value, onChange, placeholder, readOnly, textarea }) => (
  <div style={{ gridColumn: textarea ? '1 / -1' : 'auto' }}>
    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: INK_500, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        rows={2}
        style={fieldStyle(readOnly)}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        style={fieldStyle(readOnly)}
      />
    )}
  </div>
);

const fieldStyle = (readOnly) => ({
  width: '100%',
  border: `1px solid ${INK_BORDER}`,
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13.5,
  color: INK_900,
  background: readOnly ? WARM_BG : '#fff',
  outline: 'none',
  resize: 'vertical',
  fontFamily: 'inherit',
});

// ─── Signature modal ────────────────────────────────────────────────

const SignatureModal = ({ fullName, onClose, onApply }) => {
  const [tab, setTab] = useState('draw'); // draw | type | upload
  const [drawPreview, setDrawPreview] = useState(null);   // updated on pointer up
  const [typedName, setTypedName] = useState(fullName || '');
  const [uploadedDataUrl, setUploadedDataUrl] = useState(null);

  const drawCanvasRef = useRef(null);
  const typeCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const drawingRef = useRef({ drawing: false, lastX: 0, lastY: 0 });

  // ── DRAW tab ───────────────────────────────────────────────────────
  const initDrawCanvas = useCallback((canvas) => {
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#1F1D1B';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
  }, []);
  useEffect(() => {
    if (tab === 'draw') initDrawCanvas(drawCanvasRef.current);
  }, [tab, initDrawCanvas]);

  const onPointerDown = (e) => {
    const canvas = drawCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    drawingRef.current = {
      drawing: true,
      lastX: e.clientX - rect.left,
      lastY: e.clientY - rect.top,
    };
    canvas.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current.drawing) return;
    const canvas = drawCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.lastX, drawingRef.current.lastY);
    ctx.lineTo(x, y);
    ctx.stroke();
    drawingRef.current.lastX = x;
    drawingRef.current.lastY = y;
  };
  const onPointerEnd = () => {
    if (!drawingRef.current.drawing) return;
    drawingRef.current.drawing = false;
    setDrawPreview(drawCanvasRef.current.toDataURL('image/png'));
  };
  const onClearDraw = () => {
    initDrawCanvas(drawCanvasRef.current);
    setDrawPreview(null);
  };

  // ── TYPE tab ───────────────────────────────────────────────────────
  // Render the typed name to a hidden canvas in a cursive system font
  // so we can ship a PNG to the backend (consistent with draw + upload).
  const typedDataUrl = (() => {
    if (tab !== 'type' || !typedName.trim()) return null;
    const canvas = typeCanvasRef.current;
    if (!canvas) return null;
    const dpr = window.devicePixelRatio || 1;
    const W = 480;
    const H = 110;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#1F1D1B';
    // System cursive stack — looks similar across macOS / Windows / Linux.
    ctx.font = 'italic 44px "Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName.trim(), 24, H / 2);
    return canvas.toDataURL('image/png');
  })();

  // ── UPLOAD tab ─────────────────────────────────────────────────────
  // Accept any image, normalise to PNG via canvas before passing back.
  const onFilePicked = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Signature image too large (max 5 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        setUploadedDataUrl(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // ── Apply / cancel ────────────────────────────────────────────────
  const applyDisabled =
    (tab === 'draw' && !drawPreview) ||
    (tab === 'type' && !typedName.trim()) ||
    (tab === 'upload' && !uploadedDataUrl);

  const onApplyClick = () => {
    if (tab === 'draw') onApply(drawPreview, 'draw');
    else if (tab === 'type') onApply(typedDataUrl, 'type');
    else if (tab === 'upload') onApply(uploadedDataUrl, 'upload');
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, maxWidth: 560, width: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: INK_900 }}>Add your signature</h3>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, color: INK_500, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${INK_BORDER}`, marginBottom: 14 }}>
          {[
            { k: 'draw', label: 'Draw' },
            { k: 'type', label: 'Type' },
            { k: 'upload', label: 'Upload' },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              style={{
                flex: 1, padding: 9, border: 'none', background: 'none',
                fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                color: tab === t.k ? ORANGE : INK_500,
                borderBottom: tab === t.k ? `2.5px solid ${ORANGE}` : '2.5px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Draw */}
        {tab === 'draw' && (
          <div>
            <canvas
              ref={drawCanvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerLeave={onPointerEnd}
              style={{ display: 'block', width: '100%', height: 190, touchAction: 'none', cursor: 'crosshair', border: `1px solid ${INK_BORDER}`, borderRadius: 8, background: '#fff' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: INK_500 }}>
              <span>Sign above the line</span>
              <button type="button" onClick={onClearDraw} style={{ border: 'none', background: 'none', color: INK_500, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
            </div>
          </div>
        )}

        {/* Type */}
        {tab === 'type' && (
          <div>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your name"
              style={fieldStyle(false)}
            />
            <p style={{ fontSize: 11.5, color: INK_500, marginTop: 6 }}>Preview:</p>
            <div style={{ border: `1px solid ${INK_BORDER}`, borderRadius: 8, padding: 6, background: '#fff', marginTop: 4 }}>
              <canvas ref={typeCanvasRef} style={{ display: 'block', width: '100%', height: 110 }} />
            </div>
          </div>
        )}

        {/* Upload */}
        {tab === 'upload' && (
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => onFilePicked(e.target.files[0])} style={{ display: 'none' }} />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%', height: 158, border: `1.5px dashed ${INK_BORDER}`, borderRadius: 10,
                background: WARM_BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 10, cursor: 'pointer', color: INK_500, fontSize: 13.5,
              }}
            >
              <span style={{ fontSize: 26 }}>⬆</span>
              {uploadedDataUrl ? 'Replace signature image' : 'Click to upload signature image'}
            </button>
            {uploadedDataUrl && (
              <div style={{ marginTop: 10, border: `1px solid ${INK_BORDER}`, borderRadius: 8, padding: 6, background: '#fff' }}>
                <img src={uploadedDataUrl} alt="Uploaded signature preview" style={{ display: 'block', maxWidth: '100%', maxHeight: 100, margin: '0 auto' }} />
              </div>
            )}
            <p style={{ fontSize: 11.5, color: INK_500, marginTop: 8 }}>PNG or JPG, transparent background works best.</p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ border: `1px solid ${INK_BORDER}`, background: '#fff', borderRadius: 9, padding: '10px 18px', fontSize: 13.5, fontWeight: 500, color: INK_900, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApplyClick}
            disabled={applyDisabled}
            style={{
              border: 'none', background: applyDisabled ? INK_300 : ORANGE, color: '#fff',
              borderRadius: 9, padding: '10px 22px', fontSize: 13.5, fontWeight: 600,
              cursor: applyDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            Apply signature
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignAgreement;
